import "./App.css";
import React, { useCallback, useState } from "react";
import "./variables.css";
import { RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import {
  AuthProvider,
  FakeProvider,
} from "./providers/index.js";
import router from "./navigation/router.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Plugin from "./plugin/Plugin.jsx";
import {
  buttonTheme,
  cardTheme,
  colorsTheme,
  inputTheme,
  tagTheme,
} from "./theme";
import { AlertProvider, AssistanceProvider, ChimeMeetingProvider, PluginProviders } from "./plugin/providers";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n.js";
import { MeetingProvider } from "amazon-chime-sdk-component-library-react";

Amplify.configure({
  Auth: {
    Cognito: {
      region: process.env.REACT_APP_COGNITO_REGION,
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
      identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
      signUpVerificationMethod: "code",
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const webappTheme = extendTheme({
  colors: colorsTheme,
  components: {
    Button: buttonTheme,
    Card: cardTheme,
    Input: inputTheme,
    Tag: tagTheme,
  },
});

/**
 * The application entry point
 * 
 * @returns The application component
 */
function App() {
  const [isRippleEnabled, setIsRippleEnabled] = useState(false);

  /**
   * Performs a ripple effect when clicking on the screen
   */
  const performRippleEffect = useCallback((event) => {
    if (isRippleEnabled) {
      const div1 = document.createElement("div");
      const div2 = document.createElement("div");
      const div3 = document.createElement("div");

      div1.style.position = "absolute";
      div2.style.position = "absolute";
      div3.style.position = "absolute";
      div1.style.top = (event.pageY - 60) + "px";
      div1.style.left = (event.pageX - 60) + "px";
      div2.style.top = (event.pageY - 40) + "px";
      div2.style.left = (event.pageX - 40) + "px";
      div3.style.top = (event.pageY - 20) + "px";
      div3.style.left = (event.pageX - 20) + "px";
      div1.style.borderRadius = "100%";
      div2.style.borderRadius = "100%";
      div3.style.borderRadius = "100%";
      div1.style.borderStyle = "solid";
      div1.style.borderWidth = "3px";
      div2.style.borderStyle = "dashed";
      div2.style.borderWidth = "2px";
      div3.style.borderStyle = "dashed";
      div3.style.borderWidth = "1px";
      div1.style.borderColor = "#82E600";
      div2.style.borderColor = "#82E600";
      div3.style.borderColor = "#82E600";
      div1.style.zIndex = 20000;
      div2.style.zIndex = 20000;
      div3.style.zIndex = 20000;
      div1.style.width = "120px";
      div1.style.height = "120px";
      div2.style.width = "80px";
      div2.style.height = "80px";
      div3.style.width = "40px";
      div3.style.height = "40px";
      div1.classList.add("ripple");
      div2.classList.add("ripple");
      div3.classList.add("ripple");
      document.body.appendChild(div1);
      document.body.appendChild(div2);
      document.body.appendChild(div3);
      setTimeout(() => {
        document.body.removeChild(div1);
        document.body.removeChild(div2);
        document.body.removeChild(div3);
        div1.remove();
        div2.remove();
        div3.remove();
      }, 500)  
    }
  }, [isRippleEnabled]);

  return (
    <I18nextProvider i18n={i18n} defaultNS={"translations"}>
      <ChakraProvider theme={webappTheme}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <div className="App" onClick={performRippleEffect}>
              <FakeProvider isRippleEnabled={isRippleEnabled} setIsRippleEnabled={setIsRippleEnabled}>
                <AssistanceProvider>
                  <AlertProvider>
                    <MeetingProvider>
                      <ChimeMeetingProvider>
                        <RouterProvider router={router} />
                        <PluginProviders>
                          <Plugin />
                        </PluginProviders>
                      </ChimeMeetingProvider>
                    </MeetingProvider>
                  </AlertProvider>
                </AssistanceProvider>
              </FakeProvider>
            </div>
          </AuthProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </I18nextProvider>
  );
}

export default App;
